import { useMemo, useState, useRef, useEffect } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { Tag, Select, Button, Modal, Form, InputNumber, Input, Dropdown } from 'antd';
import {
  DownOutlined, CalendarOutlined, UserAddOutlined, SyncOutlined,
  WalletOutlined, SwapOutlined, RiseOutlined, ShoppingOutlined,
  UserOutlined, FileExcelOutlined, FilePdfOutlined, PlusOutlined,
  DeleteOutlined, LeftOutlined, RightOutlined, MoreOutlined, EditOutlined,
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import * as XLSX from 'xlsx';
import Calendar from '../../calendar';
import CommonTable from '../../commonTable';
import StatusAvatar from '../../statusAvatar';
import { useGetAllUserQuery } from '../../../services/user';
import {
  useGetSalesIncentiveSlabsQuery, useAddSalesIncentiveSlabMutation, useUpdateSalesIncentiveSlabMutation,
  useGetRevenueIncentiveSlabsQuery, useAddRevenueIncentiveSlabsMutation, useToggleRevenueIncentiveSlabMutation,
} from '../../../services/employee';
import usePermissions from '../../../hooks/usePermissions';
import type { EmployeeOutletContext, EmployeeIncentiveSlab, EmployeeIncentiveSlabEntry } from './types';
import './styles.scss';

/* ─── Helpers ─────────────────────────────────────────────────────────── */

const ALLOWED_CONTROL_KEYS = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'Enter'];

// Blocks any keystroke that isn't a digit (and, when `allowDecimal` is set, a
// single decimal point) — so letters can't be typed into the field at all,
// not just stripped out afterwards.
const blockNonNumericKey = (allowDecimal = false) => (e: React.KeyboardEvent<HTMLInputElement>) => {
  if (ALLOWED_CONTROL_KEYS.includes(e.key) || e.ctrlKey || e.metaKey) return;
  if (/^\d$/.test(e.key)) return;
  if (allowDecimal && e.key === '.' && !e.currentTarget.value.includes('.')) return;
  e.preventDefault();
};

const RANGE_PRESETS = [
  { label: 'Today',        getRange: () => [dayjs().startOf('day').toDate(), dayjs().endOf('day').toDate()] as [Date, Date] },
  { label: 'Yesterday',    getRange: () => [dayjs().subtract(1, 'day').startOf('day').toDate(), dayjs().subtract(1, 'day').endOf('day').toDate()] as [Date, Date] },
  { label: 'This Week',    getRange: () => [dayjs().startOf('week').toDate(), dayjs().endOf('day').toDate()] as [Date, Date] },
  { label: 'This Month',   getRange: () => [dayjs().startOf('month').toDate(), dayjs().endOf('month').toDate()] as [Date, Date] },
  { label: 'Last Month',   getRange: () => [dayjs().subtract(1, 'month').startOf('month').toDate(), dayjs().subtract(1, 'month').endOf('month').toDate()] as [Date, Date] },
  { label: 'All Time',     getRange: () => [dayjs('2000-01-01').toDate(), dayjs().endOf('day').toDate()] as [Date, Date] },
];

const SALE_TITLE_OPTIONS = [
  'New client',
  'New client - upgrade',
  'New client - balance clear',
  'New client- transfer fee',
  'New client- club transfer',
  'Renew',
  'Renew - upgrade',
  'Renew - balance clear',
  'Renew - club transfer',
  'Renew - transfer fee',
].map(v => ({ value: v, label: v }));

interface SaleTitleEntry {
  id:         string;
  saleTitle:  string;
  from:       number;
  to:         number;
  percentage: number;
  status?:    string;
}

const SALES_SLAB_UPDATE_PERM_KEY = 'INCENTIVE_SALES_SLAB_UPDATE';
const SLAB_STATUS_OPTIONS = [
  { value: 'active',   label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

type SaleTitleFormValues = { saleTitle: string; from: number; to: number; percentage: number; status?: string };

const SALE_TYPE_BOXES = [
  { key: 'new_client',    label: 'New Client',    icon: <UserAddOutlined />, color: '#6366f1' },
  { key: 'renew',         label: 'Renew',         icon: <SyncOutlined />,    color: '#0ea5e9' },
  { key: 'balance_clear', label: 'Balance Clear', icon: <WalletOutlined />,  color: '#10b981' },
  { key: 'club_transfer', label: 'Club Transfer', icon: <SwapOutlined />,    color: '#f59e0b' },
  { key: 'upgrade',       label: 'Upgrade',       icon: <RiseOutlined />,    color: '#ec4899' },
];

interface SaleRecord {
  id:            string;
  branch:        string;
  name:          string;
  profile?:      string;
  status?:       string;
  planName:      string;
  remainingDays: string;
  saleType?:     string;
  saleTitle?:    string;
  saleDate?:     string;
}

const columns = [
  { title: 'Branch', dataIndex: 'branch', key: 'branch', width: 160, render: (v?: string) => v || '-' },
  { title: 'Name',   dataIndex: 'name',   key: 'name',   width: 160, render: (v?: string) => v || '-' },
  {
    title:  'Profile',
    dataIndex: 'profile',
    key:    'profile',
    width:  80,
    align:  'center' as const,
    render: (v: string, r: SaleRecord) => <StatusAvatar src={v} status={r.status} />,
  },
  { title: 'Membership Package', dataIndex: 'planName', key: 'planName', width: 180, render: (v?: string) => v || '-' },
  { title: 'R.Days', dataIndex: 'remainingDays', key: 'remainingDays', width: 120, render: (v?: string) => v || '-' },
  {
    title:  'Invoice Status',
    dataIndex: 'saleType',
    key:    'saleType',
    width:  150,
    render: (v?: string) => v ? <Tag color="purple">{v.replace(/_/g, ' ').toUpperCase()}</Tag> : '-',
  },
  { title: 'Sale Title', dataIndex: 'saleTitle', key: 'saleTitle', width: 180, render: (v?: string) => v || '-' },
];

const getSaleTitleColumns = (canEdit: boolean, onEdit: (entry: SaleTitleEntry) => void) => [
  { title: 'S.No', key: 'sno', width: 70, align: 'center' as const, render: (_: unknown, __: SaleTitleEntry, i: number) => i + 1 },
  { title: 'Sale Title', dataIndex: 'saleTitle', key: 'saleTitle', render: (v?: string) => v || '-' },
  { title: 'From (Days)', dataIndex: 'from', key: 'from', width: 120, align: 'center' as const },
  { title: 'To (Days)', dataIndex: 'to', key: 'to', width: 120, align: 'center' as const },
  {
    title: 'Percentage',
    dataIndex: 'percentage',
    key: 'percentage',
    width: 120,
    align: 'center' as const,
    render: (v: number) => <strong style={{ color: 'var(--accent)' }}>{v}%</strong>,
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    width: 110,
    align: 'center' as const,
    render: (v?: string) => <Tag color={v === 'inactive' ? 'red' : 'green'}>{v === 'inactive' ? 'Inactive' : 'Active'}</Tag>,
  },
  ...(canEdit ? [{
    title: 'Actions',
    key: 'actions',
    width: 90,
    align: 'center' as const,
    render: (_: unknown, entry: SaleTitleEntry) => (
      <Dropdown
        trigger={['click']}
        placement="bottomRight"
        menu={{
          items: [{
            key: 'edit',
            label: <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><EditOutlined /> Edit</span>,
            onClick: () => onEdit(entry),
          }],
        }}
      >
        <Button type="text" size="small" icon={<MoreOutlined />} />
      </Dropdown>
    ),
  }] : []),
];

/* ─── Component ───────────────────────────────────────────────────────── */

interface AddSlabValues { slabs: EmployeeIncentiveSlab[]; }

const EmployeeIncentiveCalculation = () => {
  const { employee } = useOutletContext<EmployeeOutletContext>();
  const salesPersonId: string | undefined = employee?.user?._id;
  const { id: routeEmployeeId } = useParams();
  const employeeId: string | undefined = routeEmployeeId || employee?._id;

  const { data: saleTitlesResp, isFetching: saleTitlesLoading } = useGetSalesIncentiveSlabsQuery(employeeId as string, { skip: !employeeId });
  const [addSalesIncentiveSlab, { isLoading: addingSaleTitle }] = useAddSalesIncentiveSlabMutation();
  const [updateSalesIncentiveSlab, { isLoading: updatingSaleTitle }] = useUpdateSalesIncentiveSlabMutation();

  // Revenue incentive slab sets (GET list, POST new set, PATCH toggle active/inactive).
  const { data: revenueSlabsResp, isFetching: revenueSlabsLoading } = useGetRevenueIncentiveSlabsQuery(employeeId as string, { skip: !employeeId });
  const [addRevenueIncentiveSlabs, { isLoading: addingSlab }] = useAddRevenueIncentiveSlabsMutation();
  const [toggleRevenueIncentiveSlab, { isLoading: togglingSlab }] = useToggleRevenueIncentiveSlabMutation();
  const incentiveSlabs: EmployeeIncentiveSlabEntry[] = useMemo(() => {
    const r: any = revenueSlabsResp;
    const list: any[] = Array.isArray(r) ? r
      : Array.isArray(r?.data) ? r.data
      : Array.isArray(r?.data?.incentives) ? r.data.incentives
      : Array.isArray(r?.incentives) ? r.incentives
      : [];
    return list
      .filter((x: any) => x && Array.isArray(x.slabs))
      .map((x: any, i: number): EmployeeIncentiveSlabEntry => ({
        id:        x._id ?? x.id ?? `rev-slab-${i}`,
        label:     x.createdAt ? dayjs(x.createdAt).format('DD MMM YYYY') : `Slab set ${i + 1}`,
        time:      x.createdAt ? dayjs(x.createdAt).format('hh:mm A') : '',
        status:    x.status === 'active' ? 'active' : 'inactive',
        createdBy: x.createdBy?.name ?? (typeof x.createdBy === 'string' ? '' : ''),
        slabs:     x.slabs.map((sl: any) => ({
          from:                Number(sl.from ?? 0),
          to:                  Number(sl.to ?? 0),
          percentage:          Number(sl.percentage ?? 0),
          rewardAmount:        sl.rewardOfMonth ?? sl.rewardAmount ?? undefined,
          benchmarkRewardName: sl.benchmarkRewardName || undefined,
        })),
      }));
  }, [revenueSlabsResp]);
  const { hasPermission } = usePermissions();
  const saleTitleEntries: SaleTitleEntry[] = useMemo(() => {
    const r: any = saleTitlesResp;
    const list: any[] = Array.isArray(r) ? r
      : Array.isArray(r?.data) ? r.data
      : Array.isArray(r?.data?.slabs) ? r.data.slabs
      : Array.isArray(r?.slabs) ? r.slabs
      : [];
    return list.map((x: any, i: number) => ({
      id:         x._id ?? x.id ?? `sale-title-${i}`,
      saleTitle:  x.saleTitle ?? '-',
      from:       Number(x.fromDays ?? x.from ?? 0),
      to:         Number(x.toDays ?? x.to ?? 0),
      percentage: Number(x.percentage ?? 0),
      status:     x.status ?? (x.isActive === false ? 'inactive' : 'active'),
    }));
  }, [saleTitlesResp]);

  const [range, setRange] = useState<{ start: Dayjs; end: Dayjs }>({
    start: dayjs().startOf('month'),
    end: dayjs().endOf('month'),
  });
  const [calOpen, setCalOpen] = useState(false);
  const calRef = useRef<HTMLDivElement>(null);
  const [saleTypeFilter, setSaleTypeFilter] = useState('all');
  const [slabHistoryOpen, setSlabHistoryOpen] = useState(false);
  const [addSlabOpen, setAddSlabOpen] = useState(false);
  const [slabPage, setSlabPage] = useState(1);
  const [slabForm] = Form.useForm<AddSlabValues>();
  const SLAB_PAGE_SIZE = 6;

  const [saleTitleListOpen, setSaleTitleListOpen] = useState(false);
  const [addSaleTitleOpen, setAddSaleTitleOpen] = useState(false);
  const [saleTitleForm] = Form.useForm<SaleTitleFormValues>();
  const [editingSlab, setEditingSlab] = useState<SaleTitleEntry | null>(null);

  useEffect(() => {
    if (!calOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (calRef.current && !calRef.current.contains(e.target as Node)) setCalOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [calOpen]);

  const { data, isFetching } = useGetAllUserQuery(
    { salesPersonId: salesPersonId ? [salesPersonId] : undefined, limit: 1000, page: 1 } as any,
    { skip: !salesPersonId },
  );

  // ── Map the raw API response into sale records, filtered to this period. ──
  // Sale date isn't separately exposed by this endpoint — the membership's
  // start date is used as the closest available proxy for "when this sale happened".
  const allSales: SaleRecord[] = useMemo(() => {
    const users: any[] = (data as any)?.users ?? [];
    return users.map((u: any): SaleRecord => {
      const member            = u.member            || {};
      const currentMembership = u.currentMembership || {};
      return {
        id:            u._id,
        branch:        u.branchIds?.length > 0 ? u.branchIds[0]?.name : '-',
        name:          u.name,
        profile:       member.photo,
        status:        u.status,
        planName:      currentMembership.planName || 'N/A',
        remainingDays: currentMembership.totalDays != null && currentMembership.remainingDays !== undefined
          ? `${currentMembership.totalDays}/${currentMembership.remainingDays}` : '-',
        saleType:      currentMembership.saleType,
        saleTitle:     currentMembership.invoiceStatus?.saleTitle ?? currentMembership.saleTitle ?? undefined,
        saleDate:      currentMembership.startDate,
      };
    });
  }, [data]);

  const periodSales = useMemo(() => allSales.filter(s => {
    if (!s.saleDate) return false;
    const d = dayjs(s.saleDate);
    return d.isValid() && !d.isBefore(range.start, 'day') && !d.isAfter(range.end, 'day');
  }), [allSales, range]);

  const tableSales = useMemo(
    () => saleTypeFilter === 'all' ? periodSales : periodSales.filter(s => s.saleType === saleTypeFilter),
    [periodSales, saleTypeFilter],
  );

  const saleTypeOptions = [
    { value: 'all', label: 'All Status' },
    ...SALE_TYPE_BOXES.map(b => ({ value: b.key, label: b.label })),
  ];

  const countByType = (type: string) => periodSales.filter(s => s.saleType === type).length;

  const boxes = [
    ...SALE_TYPE_BOXES.map(b => ({ key: b.key, label: b.label, value: countByType(b.key), icon: b.icon, color: b.color })),
    { key: 'total', label: 'Total Sale', value: periodSales.length, icon: <ShoppingOutlined />, color: 'var(--accent)' },
  ];

  const handleExportExcel = () => {
    if (tableSales.length === 0) return;
    const rows = tableSales.map(s => ({
      Branch:              s.branch,
      Name:                s.name,
      'Membership Package': s.planName,
      'R.Days':            s.remainingDays,
      'Invoice Status':    s.saleType ? s.saleType.replace(/_/g, ' ').toUpperCase() : '-',
      'Sale Title':        s.saleTitle || '-',
    }));
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook  = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Incentive Calculation');
    XLSX.writeFile(workbook, `Incentive-Calculation-${range.start.format('YYYY-MM-DD')}-to-${range.end.format('YYYY-MM-DD')}.xlsx`);
  };

  const handleExportPDF = () => window.print();

  const handleAddSlab = async (values: AddSlabValues) => {
    if (!employeeId) return;
    try {
      await addRevenueIncentiveSlabs({
        employeeId,
        slabs: (values.slabs || []).map(s => ({
          from:       Number(s.from),
          to:         Number(s.to),
          percentage: Number(s.percentage),
          ...(s.rewardAmount != null && String(s.rewardAmount) !== '' ? { rewardOfMonth: Number(s.rewardAmount) } : {}),
          ...(s.benchmarkRewardName ? { benchmarkRewardName: s.benchmarkRewardName } : {}),
        })),
      }).unwrap();
      setAddSlabOpen(false);
      slabForm.resetFields();
    } catch {
      /* error surfaced by the global bad-request handler */
    }
  };

  const handleToggleSlabStatus = async (entry: EmployeeIncentiveSlabEntry) => {
    if (!employeeId) return;
    try {
      await toggleRevenueIncentiveSlab({
        employeeId,
        incentiveId: entry.id,
        status: entry.status === 'active' ? 'inactive' : 'active',
      }).unwrap();
    } catch {
      /* error surfaced by the global bad-request handler */
    }
  };

  const closeSaleTitleForm = () => {
    setAddSaleTitleOpen(false);
    setEditingSlab(null);
    saleTitleForm.resetFields();
  };

  const openEditSaleTitle = (entry: SaleTitleEntry) => {
    setEditingSlab(entry);
    setAddSaleTitleOpen(true);
  };

  const handleAddSaleTitle = async (values: SaleTitleFormValues) => {
    if (!employeeId) return;
    const body = {
      saleTitle:  values.saleTitle,
      fromDays:   Number(values.from),
      toDays:     Number(values.to),
      percentage: Number(values.percentage),
    };
    try {
      if (editingSlab) {
        await updateSalesIncentiveSlab({
          employeeId,
          slabId: editingSlab.id,
          ...body,
          status: values.status || editingSlab.status || 'active',
        }).unwrap();
      } else {
        await addSalesIncentiveSlab({ employeeId, ...body }).unwrap();
      }
      closeSaleTitleForm();
    } catch {
      /* error surfaced by the global bad-request handler */
    }
  };

  return (
    <div className="employee-incentive-calculation">
      <div className="eic-head">
        <div className="eic-head-top">
          <div>
            <h3 className="eic-title">Incentive Calculation</h3>
            <p className="eic-sub">Performance-based incentive for the selected period</p>
          </div>
          <div className="eic-active-clients">
            <UserOutlined className="eic-active-clients-icon" />
            <span className="eic-active-clients-value">{periodSales.length}</span>
            <span className="eic-active-clients-text">Active Client{periodSales.length === 1 ? '' : 's'}</span>
          </div>
        </div>

        <div className="eic-head-actions">
          <div className="eic-cal-wrap" ref={calRef}>
            <button className="eic-cal-trigger" onClick={() => setCalOpen(o => !o)}>
              <CalendarOutlined /> <strong>{range.start.format('DD MMM')} – {range.end.format('DD MMM YYYY')}</strong>
              <DownOutlined className="eic-cal-caret" />
            </button>
            {calOpen && (
              <div className="eic-cal-popup" onClick={e => e.stopPropagation()}>
                <Calendar
                  rangePresets={RANGE_PRESETS}
                  selectedDate={{ start: range.start.toDate(), end: range.end.toDate() }}
                  onDateChange={(v: any) => {
                    if (v?.start && v?.end) {
                      setRange({ start: dayjs(v.start), end: dayjs(v.end) });
                      setCalOpen(false);
                    }
                  }}
                />
              </div>
            )}
          </div>
          <Button type="primary" className="eic-slab-btn" onClick={() => setSlabHistoryOpen(true)}>
            Incentive Slab
          </Button>
          <Button type="primary" className="eic-slab-btn" onClick={() => setSaleTitleListOpen(true)}>
            Sale Title
          </Button>
          <Button
            className="eic-export-btn"
            icon={<FileExcelOutlined />}
            disabled={tableSales.length === 0}
            onClick={handleExportExcel}
          >
            Excel
          </Button>
          <Button
            className="eic-export-btn"
            icon={<FilePdfOutlined />}
            disabled={tableSales.length === 0}
            onClick={handleExportPDF}
          >
            PDF
          </Button>
          <Select
            value={saleTypeFilter}
            onChange={(v) => setSaleTypeFilter(v)}
            className="eic-status-select"
            style={{ width: 170, height: 41 }}
            options={saleTypeOptions}
          />
        </div>
      </div>

      <div className="eic-boxes">
        {boxes.map(b => (
          <div
            className="eic-box"
            style={{ background: `color-mix(in srgb, ${b.color} 14%, var(--hover-bg))`, borderColor: b.color }}
            key={b.key}
          >
            <span
              className="eic-box-label"
              style={{ background: `color-mix(in srgb, ${b.color} 20%, var(--card-bg))`, color: b.color }}
            >
              {b.label}
            </span>
            <div className="eic-box-body">
              <span className="eic-box-icon" style={{ background: `color-mix(in srgb, ${b.color} 18%, transparent)`, color: b.color }}>
                {b.icon}
              </span>
              <div className="eic-box-value" style={{ color: b.color }}>{b.value}</div>
            </div>
          </div>
        ))}
      </div>

      <CommonTable
        columns={columns}
        dataSource={tableSales}
        loading={isFetching}
        pagination={false}
        rowKey={(r: SaleRecord) => r.id}
        scroll={{ x: 900 }}
      />

      {/* ── Incentive Slab history modal (looks like the trainer's, own styles) ── */}
      <Modal
        open={slabHistoryOpen}
        onCancel={() => setSlabHistoryOpen(false)}
        footer={null}
        width={860}
        className="eic-rv-history-modal"
        title={
          <div className="eic-rv-history-head">
            <span>Incentive Slabs</span>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddSlabOpen(true)}>
              Add Incentive
            </Button>
          </div>
        }
      >
        {revenueSlabsLoading && incentiveSlabs.length === 0 ? (
          <div className="eic-rv-hist-empty">Loading…</div>
        ) : incentiveSlabs.length === 0 ? (
          <div className="eic-rv-hist-empty">No incentive slabs added yet.</div>
        ) : (
          <>
            <div className="eic-rv-cards">
              {incentiveSlabs.slice((slabPage - 1) * SLAB_PAGE_SIZE, slabPage * SLAB_PAGE_SIZE).map((entry) => (
                <div className={`eic-rv-card ${entry.status === 'active' ? 'is-active' : ''}`} key={entry.id}>
                  <div className="eic-rv-card-head">
                    <div className="eic-rv-card-meta">
                      <span className="eic-rv-card-month">
                        <CalendarOutlined /> {entry.label}
                        {entry.time && <span className="eic-rv-card-time">{entry.time}</span>}
                      </span>
                      {entry.createdBy && <span className="eic-rv-card-by">{entry.createdBy}</span>}
                    </div>
                    <span className={`eic-rv-status eic-rv-status-${entry.status}`}>{entry.status}</span>
                  </div>
                  <div className="eic-rv-card-slabs">
                    {entry.slabs.map((s, j) => (
                      <div className="eic-rv-slab-chip" key={j}>
                        <div className="eic-rv-slab-range">
                          <span className="eic-rv-slab-cap">Range</span>
                          <span className="eic-rv-slab-val">
                            ₹{Number(s.from).toLocaleString('en-IN')} – ₹{Number(s.to).toLocaleString('en-IN')}
                          </span>
                          {(s.rewardAmount != null || s.benchmarkRewardName) && (
                            <span className="eic-rv-slab-sub">
                              {s.rewardAmount != null && <>Reward ₹{Number(s.rewardAmount).toLocaleString('en-IN')}</>}
                              {s.rewardAmount != null && s.benchmarkRewardName && ' · '}
                              {s.benchmarkRewardName && <>🏅 {s.benchmarkRewardName}</>}
                            </span>
                          )}
                        </div>
                        <span className="eic-rv-slab-pct">{s.percentage}%</span>
                      </div>
                    ))}
                  </div>
                  <div className="eic-rv-card-foot">
                    <Button
                      size="small"
                      className={`eic-rv-toggle-btn ${entry.status === 'active' ? 'deactivate' : 'activate'}`}
                      loading={togglingSlab}
                      onClick={() => handleToggleSlabStatus(entry)}
                    >
                      {entry.status === 'active' ? 'Deactivate' : 'Activate'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {incentiveSlabs.length > SLAB_PAGE_SIZE && (
              <div className="eic-rv-pager">
                <Button
                  size="small"
                  icon={<LeftOutlined />}
                  disabled={slabPage === 1}
                  onClick={() => setSlabPage(p => Math.max(1, p - 1))}
                />
                <span className="eic-rv-pager-info">
                  Page {slabPage} of {Math.ceil(incentiveSlabs.length / SLAB_PAGE_SIZE)}
                </span>
                <Button
                  size="small"
                  icon={<RightOutlined />}
                  disabled={slabPage >= Math.ceil(incentiveSlabs.length / SLAB_PAGE_SIZE)}
                  onClick={() => setSlabPage(p => Math.min(Math.ceil(incentiveSlabs.length / SLAB_PAGE_SIZE), p + 1))}
                />
              </div>
            )}
          </>
        )}
      </Modal>

      {/* ── Add Incentive Slab modal ── */}
      <Modal
        title="Add Incentive"
        open={addSlabOpen}
        onCancel={() => { setAddSlabOpen(false); slabForm.resetFields(); }}
        onOk={() => slabForm.submit()}
        okText="Add"
        confirmLoading={addingSlab}
        width={1000}
        className="eic-add-slab-modal"
        destroyOnClose
      >
        <Form
          form={slabForm}
          layout="vertical"
          onFinish={handleAddSlab}
          initialValues={{ slabs: [{}] }}
        >
          <label className="eic-slab-label">Revenue Sharing Slabs</label>
          <Form.List name="slabs">
            {(fields, { add, remove }) => (
              <div className="eic-slab-rows">
                {fields.map((field, idx) => (
                  <div className="eic-slab-row-card" key={field.key}>
                    <div className="eic-slab-row-head">
                      <span>Sr. No. {idx + 1}</span>
                      {fields.length > 1 && (
                        <Button className="eic-slab-del-btn" icon={<DeleteOutlined />} onClick={() => remove(field.name)} />
                      )}
                    </div>
                    <div className="eic-slab-row-grid">
                      <Form.Item label="Revenue Bracket — From (₹)" name={[field.name, 'from']} rules={[{ required: true, message: 'Required' }]}>
                        <InputNumber
                          style={{ width: '100%' }}
                          min={0}
                          placeholder="e.g. 400000"
                          onKeyDown={blockNonNumericKey()}
                          parser={(value) => (value ? value.replace(/[^\d]/g, '') : '') as unknown as number}
                        />
                      </Form.Item>
                      <Form.Item label="Revenue Bracket — To (₹)" name={[field.name, 'to']} rules={[{ required: true, message: 'Required' }]}>
                        <InputNumber
                          style={{ width: '100%' }}
                          min={0}
                          placeholder="e.g. 499999"
                          onKeyDown={blockNonNumericKey()}
                          parser={(value) => (value ? value.replace(/[^\d]/g, '') : '') as unknown as number}
                        />
                      </Form.Item>
                      <Form.Item label="Revenue Sharing %" name={[field.name, 'percentage']} rules={[{ required: true, message: 'Required' }]}>
                        <InputNumber
                          style={{ width: '100%' }}
                          min={0}
                          max={100}
                          placeholder="e.g. 1.5"
                          onKeyDown={blockNonNumericKey(true)}
                          parser={(value) => (value ? value.replace(/[^\d.]/g, '') : '') as unknown as number}
                        />
                      </Form.Item>
                      <Form.Item label="Reward of the Month (₹)" name={[field.name, 'rewardAmount']}>
                        <InputNumber
                          style={{ width: '100%' }}
                          min={0}
                          placeholder="e.g. 5000"
                          onKeyDown={blockNonNumericKey()}
                          parser={(value) => (value ? value.replace(/[^\d]/g, '') : '') as unknown as number}
                        />
                      </Form.Item>
                      <Form.Item label="Benchmark Reward Name" name={[field.name, 'benchmarkRewardName']}>
                        <Input placeholder="e.g. Silver Achiever" />
                      </Form.Item>
                    </div>
                  </div>
                ))}
                <Button type="dashed" block icon={<PlusOutlined />} onClick={() => add()}>
                  Add Slab
                </Button>
              </div>
            )}
          </Form.List>
        </Form>
      </Modal>

      {/* ── Sale Title list modal ── */}
      <Modal
        open={saleTitleListOpen}
        onCancel={() => setSaleTitleListOpen(false)}
        footer={null}
        width={720}
        className="eic-sale-title-modal"
        title={
          <div className="eic-slab-modal-head">
            <span>Sale Title</span>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditingSlab(null); setAddSaleTitleOpen(true); }}>
              Add
            </Button>
          </div>
        }
      >
        <CommonTable
          columns={getSaleTitleColumns(hasPermission(SALES_SLAB_UPDATE_PERM_KEY), openEditSaleTitle)}
          dataSource={saleTitleEntries}
          loading={saleTitlesLoading}
          pagination={false}
          rowKey={(r: SaleTitleEntry) => r.id}
          locale={{ emptyText: 'No sale title slabs added yet.' }}
        />
      </Modal>

      {/* ── Add Sale Title modal ── */}
      <Modal
        title={editingSlab ? 'Edit Sale Title' : 'Add Sale Title'}
        open={addSaleTitleOpen}
        onCancel={closeSaleTitleForm}
        onOk={() => saleTitleForm.submit()}
        okText={editingSlab ? 'Update' : 'Add'}
        confirmLoading={addingSaleTitle || updatingSaleTitle}
        className="eic-add-sale-title-modal"
        width={760}
        destroyOnClose
      >
        <Form
          key={editingSlab?.id ?? 'new'}
          form={saleTitleForm}
          layout="vertical"
          onFinish={handleAddSaleTitle}
          initialValues={editingSlab ? {
            saleTitle:  editingSlab.saleTitle,
            from:       editingSlab.from,
            to:         editingSlab.to,
            percentage: editingSlab.percentage,
            status:     editingSlab.status || 'active',
          } : undefined}
        >
          <div className="eic-sale-title-form-grid">
            <Form.Item label="Sale Title" name="saleTitle" rules={[{ required: true, message: 'Please select a sale title' }]}>
              <Select size="large" placeholder="Select sale title" options={SALE_TITLE_OPTIONS} showSearch optionFilterProp="label" />
            </Form.Item>
            <Form.Item label="From (Days)" name="from" rules={[{ required: true, message: 'Required' }]}>
              <InputNumber
                size="large"
                style={{ width: '100%' }}
                min={0}
                placeholder="e.g. 400000"
                onKeyDown={blockNonNumericKey()}
                parser={(value) => (value ? value.replace(/[^\d]/g, '') : '') as unknown as number}
              />
            </Form.Item>
            <Form.Item label="To (Days)" name="to" rules={[{ required: true, message: 'Required' }]}>
              <InputNumber
                size="large"
                style={{ width: '100%' }}
                min={0}
                placeholder="e.g. 499999"
                onKeyDown={blockNonNumericKey()}
                parser={(value) => (value ? value.replace(/[^\d]/g, '') : '') as unknown as number}
              />
            </Form.Item>
            <Form.Item
              label="Percentage"
              name="percentage"
              rules={[
                { required: true, message: 'Required' },
                { type: 'number', min: 1, max: 100, message: 'Enter 1 – 100' },
              ]}
            >
              <InputNumber
                size="large"
                className="eic-percent-input"
                style={{ width: '100%' }}
                min={1}
                max={100}
                step={1}
                controls
                placeholder="1 – 100"
                onKeyDown={blockNonNumericKey(true)}
                parser={(value) => (value ? value.replace(/[^\d.]/g, '') : '') as unknown as number}
              />
            </Form.Item>
            {editingSlab && (
              <Form.Item label="Status" name="status" rules={[{ required: true, message: 'Required' }]}>
                <Select size="large" options={SLAB_STATUS_OPTIONS} />
              </Form.Item>
            )}
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default EmployeeIncentiveCalculation;
